import React, { useState, useEffect, useCallback } from 'react';
import Pagination from './Pagination';
import './ManualBetLogs.css';

const API_BASE = '/api';

function ManualBetLogs() {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Default dates: current month 1st → today
  const getDefaultDates = () => {
    const now = new Date();
    const y = now.getFullYear(), m = String(now.getMonth()+1).padStart(2,'0'), d = String(now.getDate()).padStart(2,'0');
    return { start: `${y}-${m}-01`, end: `${y}-${m}-${d}` };
  };
  const defaults = getDefaultDates();

  // Filters
  const [filterSuccess, setFilterSuccess] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterAccount, setFilterAccount] = useState('');
  const [filterStartDate, setFilterStartDate] = useState(defaults.start);
  const [filterEndDate, setFilterEndDate] = useState(defaults.end);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [displayNameMap, setDisplayNameMap] = useState({});

  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const res = await fetch(`${API_BASE}/nesine-accounts`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.success) {
          setAccounts(data.data.map(a => a.username));
          const dnMap = {};
          data.data.forEach(a => { if (a.displayName) dnMap[a.username] = a.displayName; });
          setDisplayNameMap(dnMap);
        }
      } catch {}
    };
    fetchAccounts();
  }, [token]);

  const fetchLogs = useCallback(async (page = 1, useFilters = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', 50);
      if (useFilters) {
        if (filterSuccess !== '') params.set('success', filterSuccess);
        if (filterTeam.trim()) params.set('team', filterTeam.trim());
        if (filterAccount) params.set('nesineUser', filterAccount);
        if (filterStartDate) params.set('startDate', filterStartDate);
        if (filterEndDate) params.set('endDate', filterEndDate);
      }

      const res = await fetch(`${API_BASE}/goals/manual-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setLogs(data.data);
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error('Manual logs fetch error:', err);
    }
    setLoading(false);
  }, [token, filterSuccess, filterTeam, filterAccount, filterStartDate, filterEndDate]);

  useEffect(() => {
    fetchLogs(1, false);
  }, []); // eslint-disable-line

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchLogs(newPage, filtersApplied);
    setExpandedRows(new Set());
  };

  const handleFilter = () => {
    setFiltersApplied(true);
    fetchLogs(1, true);
    setExpandedRows(new Set());
  };

  const handleClearFilters = () => {
    setFilterSuccess('');
    setFilterTeam('');
    setFilterAccount('');
    setFilterStartDate(defaults.start);
    setFilterEndDate(defaults.end);
    setFiltersApplied(false);
    fetchLogs(1, false);
    setExpandedRows(new Set());
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '-';
    const date = new Date(isoStr);
    return date.toLocaleString('tr-TR', {
      timeZone: 'Europe/Istanbul',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const parseTeamName = (fullName) => {
    const match = fullName?.match(/^(.+?)\s*\(([^)]+)\)$/);
    if (match) return { club: match[1].trim(), player: match[2].trim() };
    return { club: '', player: fullName || '?' };
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getOutcomeLabel = (n) => {
    if (n === 1) return 'Ev Sahibi';
    if (n === 2) return 'Gol Yok';
    if (n === 3) return 'Deplasman';
    return String(n);
  };

  return (
    <div className="mbl">
      {/* Header */}
      <div className="mbl-header">
        <div className="mbl-header-left">
          <h2>🎮 Manuel Kupon Logları</h2>
          {pagination.total > 0 && (
            <span className="mbl-count">{pagination.total} kayıt</span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mbl-filters">
        <div className="filter-group">
          <label>Sonuç</label>
          <select value={filterSuccess} onChange={e => setFilterSuccess(e.target.value)}>
            <option value="">Tümü</option>
            <option value="true">Başarılı</option>
            <option value="false">Başarısız</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Hesap</label>
          <select value={filterAccount} onChange={e => setFilterAccount(e.target.value)}>
            <option value="">Tümü</option>
            {accounts.map(u => <option key={u} value={u}>{displayNameMap[u] || u}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Takım</label>
          <input
            type="text"
            placeholder="Takım ara..."
            value={filterTeam}
            onChange={e => setFilterTeam(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleFilter()}
          />
        </div>
        <div className="filter-group">
          <label>Başlangıç</label>
          <input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} />
        </div>
        <div className="filter-group">
          <label>Bitiş</label>
          <input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} />
        </div>
        <div className="filter-actions">
          <button className="filter-btn" onClick={handleFilter}>Filtrele</button>
          <button className="filter-clear-btn" onClick={handleClearFilters}>Temizle</button>
        </div>
      </div>

      {/* Top Pagination */}
      <Pagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        onPageChange={(p) => { handlePageChange(p); }}
      />

      {/* Table */}
      <div className="mbl-table-container">
        {loading ? (
          <div className="mbl-loading">
            <div className="spinner" />
            <p>Yükleniyor...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="mbl-empty">
            <span className="mbl-empty-icon">📭</span>
            <p>Henüz manuel kupon logu yok.</p>
          </div>
        ) : (
          <table className="mbl-table">
            <thead>
              <tr>
                <th className="mbl-th-time">Zaman</th>
                <th className="mbl-th-user">Hesap</th>
                <th className="mbl-th-home">Ev</th>
                <th className="mbl-th-away">Dep</th>
                <th className="mbl-th-score">Skor</th>
                <th className="mbl-th-sov">S.Gol</th>
                <th className="mbl-th-pick">Seçim</th>
                <th className="mbl-th-odd">Oran</th>
                <th className="mbl-th-status">Sonuç</th>
                <th className="mbl-th-errcode">Hata Kodu</th>
                <th className="mbl-th-validate">Validate</th>
                <th className="mbl-th-verrcode">V.Hata</th>
                <th className="mbl-th-barcode">Barkod</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const isExpanded = expandedRows.has(log._id);
                const hasDetail = log.playRequest || log.playResponse || log.validateRequest || log.validateResponse || log.error || (log.batches && log.batches.length > 0);
                return (
                  <React.Fragment key={log._id}>
                    <tr
                      className={`mbl-row ${log.success ? 'mbl-row-success' : 'mbl-row-fail'} ${hasDetail ? 'mbl-row-clickable' : ''}`}
                      onClick={() => hasDetail && toggleRow(log._id)}
                    >
                      <td className="mbl-td-time">{formatTime(log.createdAt)}</td>
                      <td className="mbl-td-user">{displayNameMap[log.nesineUser] || log.nesineUser || '-'}</td>
                      <td className="mbl-td-team">
                        {(() => { const t = parseTeamName(log.homeTeam); return (
                          <div className="mbl-team-cell">
                            <span className="mbl-team-player">{t.player}</span>
                            {t.club && <span className="mbl-team-club">{t.club}</span>}
                          </div>
                        ); })()}
                      </td>
                      <td className="mbl-td-team">
                        {(() => { const t = parseTeamName(log.awayTeam); return (
                          <div className="mbl-team-cell">
                            <span className="mbl-team-player">{t.player}</span>
                            {t.club && <span className="mbl-team-club">{t.club}</span>}
                          </div>
                        ); })()}
                      </td>
                      <td className="mbl-td-score">
                        <span className="mbl-score-display">
                          {log.homeScore}-{log.awayScore}
                        </span>
                      </td>
                      <td className="mbl-td-sov">
                        <span className="mbl-sov-badge">{log.sov || '-'}</span>
                      </td>
                      <td className="mbl-td-pick">
                        <span className={`mbl-pick-badge mbl-pick-${log.outcomeN}`}>
                          {getOutcomeLabel(log.outcomeN)}
                        </span>
                      </td>
                      <td className="mbl-td-odd">
                        {log.odd ? log.odd.toFixed(2) : '-'}
                      </td>
                      <td className="mbl-td-status">
                        <span className={`mbl-status-badge ${log.success ? 'mbl-status-success' : 'mbl-status-fail'}`}>
                          {log.success ? '✅ Başarılı' : '❌ Başarısız'}
                        </span>
                      </td>
                      <td className="mbl-td-errcode">
                        {log.playErrorCode ? (
                          <span className={`mbl-errcode-badge ${log.success ? 'mbl-errcode-resolved' : log.playErrorCode === '51-253' ? 'mbl-errcode-warn' : 'mbl-errcode-fail'}`}>
                            {log.playErrorCode}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="mbl-td-validate">
                        {(() => {
                          const vSuccess = log.validateSuccess != null ? log.validateSuccess
                            : log.validateResponse ? !!log.validateResponse?.data?.B : null;
                          if (vSuccess != null) return (
                            <span className={`mbl-status-badge ${vSuccess ? 'mbl-status-success' : 'mbl-status-fail'}`}>
                              {vSuccess ? '✅' : '❌'}
                            </span>
                          );
                          return '-';
                        })()}
                      </td>
                      <td className="mbl-td-verrcode">
                        {(() => {
                          const vErr = log.validateErrorCode || log.validateResponse?.exceptionInfoList?.[0]?.code || null;
                          const vSuccess = log.validateSuccess != null ? log.validateSuccess
                            : log.validateResponse ? !!log.validateResponse?.data?.B : null;
                          if (vErr) return (
                            <span className={`mbl-errcode-badge ${vSuccess ? 'mbl-errcode-resolved' : 'mbl-errcode-fail'}`}>
                              {vErr}
                            </span>
                          );
                          return '-';
                        })()}
                      </td>
                      <td className="mbl-td-barcode">
                        {log.barcode ? (
                          <span className="mbl-barcode">{log.barcode}</span>
                        ) : '-'}
                      </td>
                    </tr>
                    {hasDetail && isExpanded && (
                      <tr className="mbl-detail-row">
                        <td colSpan="13">
                          <div className={`mbl-detail-panel ${log.success ? 'mbl-panel-success' : 'mbl-panel-fail'}`}>
                            <div className="mbl-detail-header">
                              <span className={`mbl-detail-badge ${log.success ? 'mbl-badge-success' : 'mbl-badge-fail'}`}>
                                {log.success ? '✅ Kupon Oynandı' : '❌ Kupon Başarısız'}
                              </span>
                              {log.barcode && (
                                <span className="mbl-detail-barcode">Barkod: {log.barcode}</span>
                              )}
                              {log.playErrorCode && (
                                <span className={`mbl-detail-errcode ${log.success ? 'mbl-errcode-resolved' : log.playErrorCode === '51-253' ? 'mbl-errcode-warn' : 'mbl-errcode-fail'}`}>
                                  Play Hata: {log.playErrorCode} {log.success ? '(Validate ile çözüldü)' : ''}
                                </span>
                              )}
                              {log.error && (
                                <span className="mbl-detail-error">Hata: {log.error}</span>
                              )}
                              <span className="mbl-detail-meta">
                                {log.odd && `Oran: ${log.odd}`}
                                {log.amount && ` | Tutar: ${log.amount} TL`}
                                {log.potentialWin && ` | Kazanç: ${log.potentialWin} TL`}
                                {log.sov != null && ` | Sıradaki: ${log.sov}. Gol`}
                                {log.outcomeName && ` | Seçim: ${log.outcomeName}`}
                              </span>
                            </div>
                            <div className="mbl-detail-sections">
                              {log.batches && log.batches.length > 0 ? (
                                log.batches.map((batch, bi) => (
                                  <div key={bi} className="mbl-batch-block">
                                    <div className="mbl-batch-header">
                                      <span className={`mbl-batch-badge ${batch.success ? 'mbl-badge-success' : 'mbl-badge-fail'}`}>
                                        {batch.label || `Batch ${bi + 1}`}
                                      </span>
                                      <span className="mbl-batch-info">
                                        {batch.amount}₺ x{batch.count}
                                        {batch.barcode && ` | Barkod: ${batch.barcode}`}
                                        {batch.bdWait != null && ` | BD: ${batch.bdWait}ms`}
                                      </span>
                                      {batch.playErrorCode && (
                                        <span className={`mbl-detail-errcode ${batch.success ? 'mbl-errcode-resolved' : 'mbl-errcode-fail'}`}>
                                          Play: {batch.playErrorCode}
                                        </span>
                                      )}
                                      {batch.validateErrorCode && (
                                        <span className={`mbl-detail-errcode ${batch.success ? 'mbl-errcode-resolved' : 'mbl-errcode-fail'}`}>
                                          Validate: {batch.validateErrorCode}
                                        </span>
                                      )}
                                      {batch.error && !batch.success && (
                                        <span className="mbl-detail-error">{batch.error}</span>
                                      )}
                                    </div>
                                    <div className="mbl-batch-details">
                                      {batch.playRequest && (
                                        <div className="mbl-section">
                                          <h4>Play Request</h4>
                                          <pre>{JSON.stringify(batch.playRequest, null, 2)}</pre>
                                        </div>
                                      )}
                                      {batch.playResponse && (
                                        <div className="mbl-section">
                                          <h4>Play Response</h4>
                                          <pre>{JSON.stringify(batch.playResponse, null, 2)}</pre>
                                        </div>
                                      )}
                                      {batch.validateRequest && (
                                        <div className="mbl-section">
                                          <h4>Validate Request</h4>
                                          <pre>{JSON.stringify(batch.validateRequest, null, 2)}</pre>
                                        </div>
                                      )}
                                      {batch.validateResponse && (
                                        <div className="mbl-section">
                                          <h4>Validate Response</h4>
                                          <pre>{JSON.stringify(batch.validateResponse, null, 2)}</pre>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <>
                                  {log.playRequest && (
                                    <div className="mbl-section">
                                      <h4>Play Request</h4>
                                      <pre>{JSON.stringify(log.playRequest, null, 2)}</pre>
                                    </div>
                                  )}
                                  {log.playResponse && (
                                    <div className="mbl-section">
                                      <h4>Play Response</h4>
                                      <pre>{JSON.stringify(log.playResponse, null, 2)}</pre>
                                    </div>
                                  )}
                                  {log.validateRequest && (
                                    <div className="mbl-section">
                                      <h4>Validate Request</h4>
                                      <pre>{JSON.stringify(log.validateRequest, null, 2)}</pre>
                                    </div>
                                  )}
                                  {log.validateResponse && (
                                    <div className="mbl-section">
                                      <h4>Validate Response</h4>
                                      <pre>{JSON.stringify(log.validateResponse, null, 2)}</pre>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Bottom Pagination */}
      <Pagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        onPageChange={(p) => { handlePageChange(p); }}
      />
    </div>
  );
}

export default ManualBetLogs;
